import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { clearPersistedQueryCache } from './query-provider.component';

/** Where we remember which user the persisted cache belongs to (per persist key). */
const ownerKey = (persistKey: string): string => `${persistKey}:user`;

/**
 * Drop the persisted query cache when the signed-in user changes under us.
 *
 * The cache is persisted to localStorage and rehydrated synchronously on the next
 * load. That's what we want on a same-user reload, but after an account switch that
 * arrives via a cross-app redirect (add-account bounces through the auth app, which
 * can't clear this origin's storage), the browser rehydrates the PREVIOUS user's data
 * and shows it until something forces a refetch. The in-app switch path avoids this by
 * clearing + hard-reloading itself; this is the safety net for every other path.
 *
 * Once `/me` resolves, compare its user id to the one the persisted cache was written
 * for. On a mismatch, drop the persisted copy and invalidate every query so the new
 * account's data replaces the stale render. Same-user loads are a no-op.
 */
export function useEvictCacheOnUserChange(
	userId: string | null | undefined,
	persistKey: string,
): void {
	const queryClient = useQueryClient();
	useEffect(() => {
		// Effects are client-only, so window/localStorage are always present here.
		if (!userId) {
			return;
		}
		const key = ownerKey(persistKey);
		const previous = window.localStorage.getItem(key);
		if (previous && previous !== userId) {
			clearPersistedQueryCache(persistKey);
			void queryClient.invalidateQueries();
		}
		window.localStorage.setItem(key, userId);
	}, [userId, persistKey, queryClient]);
}
